// photoMarks.js — the marks of the photo mark-up tools, drawn the same on the
// screen and into the saved photo.
//
// v0.279.0 (2026-10-30). Brett: "dont forget the ability to markup and post it
// to conversation Just 3 tools, text, arrow and pen." A mark is one of
//   { kind: 'pen',   pts: [[x, y], ...] }
//   { kind: 'arrow', from: [x, y], to: [x, y] }
//   { kind: 'text',  at: [x, y], text }
// in the photo's own 0..1 coordinates. A mark with no kind is a pen stroke
// (the v0.262 register sheet's { pts }).
//
// Sizes are shares of the photo's shorter side, so a mark is the same size on
// the screen, on a phone and in the full-size file.

export const PEN_WIDTH = 0.006;
export const TEXT_SIZE = 0.045;
// An arrow shorter than this (a tap) is not kept.
export const MIN_ARROW = 0.02;
export const TEXT_MAX = 40;
export const MARK_FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif";
export const TOOLS = Object.freeze(['pen', 'arrow', 'text']);

export const kindOf = (m) => (m && (m.kind === 'arrow' || m.kind === 'text') ? m.kind : 'pen');
export const lineWidthFor = (w, h) => Math.max(2, PEN_WIDTH * Math.min(w, h));
export const fontFor = (size) => `700 ${size}px ${MARK_FONT}`;

// The words as they are kept: one line, trimmed, no longer than TEXT_MAX.
export const cleanText = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, TEXT_MAX);

export const arrowLongEnough = (m) =>
  !!m && Array.isArray(m.from) && Array.isArray(m.to) &&
  Math.hypot(m.to[0] - m.from[0], m.to[1] - m.from[1]) >= MIN_ARROW;

// The marks worth keeping: a stroke with a point, an arrow longer than a tap,
// words that are not blank.
export function usableMarks(marks) {
  return (Array.isArray(marks) ? marks : []).filter((m) => {
    const k = kindOf(m);
    if (k === 'arrow') return arrowLongEnough(m);
    if (k === 'text') return !!m && Array.isArray(m.at) && cleanText(m.text) !== '';
    return !!m && Array.isArray(m.pts) && m.pts.length > 0;
  });
}

// SVG path of a pen stroke in a w x h picture; a tap is a dot.
export const penPath = (pts, w, h) => (pts.length === 1
  ? `M${pts[0][0] * w} ${pts[0][1] * h}h0.01`
  : 'M' + pts.map(([x, y]) => `${x * w} ${y * h}`).join('L'));

// An arrow in a w x h picture: the shaft stops at the base of a solid head
// about 4.5 line widths long (never more than 60% of the arrow).
export function arrowGeometry(from, to, w, h, lineWidth) {
  const x1 = from[0] * w, y1 = from[1] * h, x2 = to[0] * w, y2 = to[1] * h;
  const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy);
  if (!(len > 0)) return null;
  const head = Math.min(len * 0.6, lineWidth * 4.5);
  const ux = dx / len, uy = dy / len;
  const bx = x2 - ux * head, by = y2 - uy * head;
  const half = head * 0.6;
  return {
    shaft: [[x1, y1], [bx, by]],
    head: [[x2, y2], [bx - uy * half, by + ux * half], [bx + uy * half, by - ux * half]],
  };
}

// Where words sit in a w x h picture: left edge at the tap, centred on it
// up and down, moved in to stay inside the photo, and made smaller only when
// they would not fit across it. `ctx` measures (a canvas 2D context).
export function textBox(ctx, text, at, w, h) {
  let size = Math.max(12, TEXT_SIZE * Math.min(w, h));
  const pad = size * 0.3;
  ctx.font = fontFor(size);
  let width = ctx.measureText(text).width;
  const room = Math.max(1, w - 2 * pad);
  if (width > room) {
    size = (size * room) / width;
    ctx.font = fontFor(size);
    width = room;
  }
  const x = Math.min(Math.max(pad, at[0] * w), Math.max(pad, w - pad - width));
  const y = Math.min(Math.max(pad + size / 2, at[1] * h), Math.max(pad + size / 2, h - pad - size / 2));
  return { x, y, size, width, halo: size * 0.18 };
}

// The marks drawn onto a canvas of the photo at w x h. `ink` is the mark
// colour, `halo` the outline round the words so they read on any photo.
export function drawMarks(ctx, marks, w, h, { ink, halo }) {
  const lw = lineWidthFor(w, h);
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const m of usableMarks(marks)) {
    const k = kindOf(m);
    if (k === 'pen') {
      ctx.strokeStyle = ink;
      ctx.lineWidth = lw;
      ctx.beginPath();
      ctx.moveTo(m.pts[0][0] * w, m.pts[0][1] * h);
      if (m.pts.length === 1) ctx.lineTo(m.pts[0][0] * w + 0.01, m.pts[0][1] * h);
      for (let i = 1; i < m.pts.length; i++) ctx.lineTo(m.pts[i][0] * w, m.pts[i][1] * h);
      ctx.stroke();
    } else if (k === 'arrow') {
      const g = arrowGeometry(m.from, m.to, w, h, lw);
      if (!g) continue;
      ctx.strokeStyle = ink;
      ctx.lineWidth = lw;
      ctx.beginPath();
      ctx.moveTo(g.shaft[0][0], g.shaft[0][1]);
      ctx.lineTo(g.shaft[1][0], g.shaft[1][1]);
      ctx.stroke();
      ctx.fillStyle = ink;
      ctx.beginPath();
      ctx.moveTo(g.head[0][0], g.head[0][1]);
      ctx.lineTo(g.head[1][0], g.head[1][1]);
      ctx.lineTo(g.head[2][0], g.head[2][1]);
      ctx.closePath();
      ctx.fill();
    } else {
      const text = cleanText(m.text);
      const b = textBox(ctx, text, m.at, w, h);
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.lineWidth = b.halo;
      ctx.strokeStyle = halo;
      ctx.strokeText(text, b.x, b.y);
      ctx.fillStyle = ink;
      ctx.fillText(text, b.x, b.y);
    }
  }
  ctx.restore();
}
